import { type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { DateTime } from 'luxon';
import type { Participation } from '../entities/Participation.js';
import type { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import type { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';

export function nachrueckPossible(
  participation: NarrowedEntity<
    Participation,
    {
      _id: 1;
      state: 1;
      userID: 1;
      nachrueckPriority: 1;
      product: {
        institutionID: 1;
        isCanceled: 1;
        leftCapacity: 1;
        nachrueckBlockTimeForLowPriorityInDays: 1;
        appointments: { start: 1 };
        settings: {
          blockAutomaticMoveUpDaysInAdvance: 1;
          inheritedBlockAutomaticMoveUpDaysInAdvance: 1;
        };
      };
    }
  >,
  now: DateTime = DateTime.now(),
) {
  if (participation.state !== ParticipationState.WAITING) return false;
  const product = participation.product;
  if (!product) throw new Error('product not set');
  if (product.leftCapacity <= 0) return false;
  if (product.isCanceled) return false;

  const startTime = product.appointments[0]?.start;

  // check last termin
  const startOfLastTermin = product.appointments[product.appointments.length - 1]?.start;
  if (startOfLastTermin) {
    if (now > startOfLastTermin) return false; // event is already over
  }

  // check blockAutomaticMoveUpDaysInAdvance
  const nachrueckBlockFrist = (() => {
    if (product.settings.blockAutomaticMoveUpDaysInAdvance) return product.settings.blockAutomaticMoveUpDaysInAdvance;
    const inheritedFrist = product.settings.inheritedBlockAutomaticMoveUpDaysInAdvance;
    if (inheritedFrist) return parseInt(inheritedFrist[1] as any);
    return null;
  })();

  if (nachrueckBlockFrist && startTime) {
    if (now > startTime.minus({ days: nachrueckBlockFrist })) return false;
  }

  if (participation.nachrueckPriority === 'high') return true;

  // check low-priority blockfrist
  if (product.nachrueckBlockTimeForLowPriorityInDays || product.nachrueckBlockTimeForLowPriorityInDays === 0) {
    const endOfRegistrationBlock = startTime?.minus({ days: product.nachrueckBlockTimeForLowPriorityInDays });
    if (!endOfRegistrationBlock) return false; // no start date yet => dont let in
    if (now < endOfRegistrationBlock) return false; // block period not over yet
  }

  return true;
}

export function getNachruecker(
  workshop: NarrowedEntity<
    Workshop,
    {
      _id: 1;
      institutionID: 1;
      leftCapacity: 1;
      settings: {
        autoNachrueckenEnabled: 1;
        blockAutomaticMoveUpDaysInAdvance: 1;
        inheritedBlockAutomaticMoveUpDaysInAdvance: 1;
      };
    }
  >,
  participations: NarrowedEntity<
    ParticipationWorkshop,
    {
      _id: 1;
      state: 1;
      userID: 1;
      nachrueckPriority: 1;
      productPriceForUser: { unitAmount: 1 };
      product: {
        isCanceled: 1;
        institutionID: 1;
        leftCapacity: 1;
        nachrueckBlockTimeForLowPriorityInDays: 1;
        appointments: { start: 1 };
        settings: {
          blockAutomaticMoveUpDaysInAdvance: 1;
          inheritedBlockAutomaticMoveUpDaysInAdvance: 1;
        };
      };
    }
  >[],
) {
  const canAutoNachruecken = [] as string[];
  const canNotAutoNachruecken = [] as typeof participations;

  for (const participation of participations) {
    if (!participation.product) throw new Error('participation has no product');
    Object.assign(participation.product, { settings: workshop.settings });

    const autoNachrueckPossible =
      nachrueckPossible(participation) &&
      canAutoNachruecken.length < workshop.leftCapacity &&
      workshop.settings.autoNachrueckenEnabled &&
      (!participation.productPriceForUser || !participation.productPriceForUser.unitAmount); // when workshop cost money, manuell nackruecken is required

    if (autoNachrueckPossible) canAutoNachruecken.push(participation._id);
    else canNotAutoNachruecken.push(participation);
  }

  const canManuallyNachruecken = [] as string[];
  const stillOnWaitinglist = [] as string[];
  for (const participation of canNotAutoNachruecken) {
    if (!participation.product) throw new Error('participation has no product');
    Object.assign(participation.product, { settings: workshop.settings });

    const manuallNachrueckPossible =
      nachrueckPossible(participation) && canAutoNachruecken.length < workshop.leftCapacity; // when auto nachruecken took all spots manuall nachrueck is not posssible anymore

    if (manuallNachrueckPossible) canManuallyNachruecken.push(participation._id);
    else stillOnWaitinglist.push(participation._id);
  }

  return {
    canManuallyNachruecken,
    canAutoNachruecken,
    stillOnWaitinglist,
  };
}
